/**
 * RFC-114 LoR4 — Floor Selector
 *
 * Vertical elevator-panel style floor selector for interior navigation.
 * Renders when activeLoR >= 4 and at least one storey is available.
 *
 * Architecture reference: MULTI_SCALE_LOR_ARCHITECTURE.md §7.3 (Storey/Floor Selection)
 */
import type { CSSProperties } from 'react';
import { tokens } from '@/design';

export interface FloorSelectorProps {
  /** Current active LoR (renders only when >= 4). */
  readonly activeLoR: number;
  /** Sorted storey labels, e.g. ["B1", "GF", "1", "2", "3"]. */
  readonly storeys: string[];
  /** Currently selected storey label. */
  readonly activeStorey: string;
  /** Callback when a floor button is clicked. */
  readonly onSelectStorey: (storey: string) => void;
}

export function FloorSelector({ activeLoR, storeys, activeStorey, onSelectStorey }: FloorSelectorProps) {
  if (activeLoR < 4 || storeys.length === 0) return null;

  return (
    <div
      style={containerStyle}
      role="navigation"
      aria-label="Floor selector"
      data-testid="lor-floor-selector"
    >
      <div style={labelStyle} aria-hidden="true">Floors</div>
      {[...storeys].reverse().map((storey) => (
        <FloorButton
          key={storey}
          label={storey}
          active={storey === activeStorey}
          onClick={() => onSelectStorey(storey)}
        />
      ))}
    </div>
  );
}

interface FloorButtonProps {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}

function FloorButton({ label, active, onClick }: FloorButtonProps) {
  return (
    <button
      type="button"
      style={active ? activeButtonStyle : buttonStyle}
      onClick={onClick}
      aria-label={`Go to floor ${label}`}
      aria-pressed={active}
      data-testid={`lor-floor-${label}`}
    >
      {label}
    </button>
  );
}

const containerStyle: CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  zIndex: (tokens.zIndex?.panel ?? 200) + 1,
  pointerEvents: 'auto',
};

const labelStyle: CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: tokens.color.ink.muted,
  marginBottom: 2,
};

const buttonBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 28,
  borderRadius: tokens.radius.sm,
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.02em',
  transition: `background ${tokens.motion.duration.fast}ms ease, color ${tokens.motion.duration.fast}ms ease`,
};

const buttonStyle: CSSProperties = {
  ...buttonBase,
  background: tokens.color.surface[3],
  color: tokens.color.ink.secondary,
};

const activeButtonStyle: CSSProperties = {
  ...buttonBase,
  background: tokens.color.accent.primary,
  color: tokens.color.accent.on,
};
